import React from "react";
import DashboardPanel from "./DashboardPanel";

const PRIORITY_ORDER = ["Critical", "High", "Medium", "Low", "Unset"];
const PRIORITY_BARS = {
  Critical: "bg-rose-500",
  High: "bg-red-400",
  Medium: "bg-orange-400",
  Low: "bg-emerald-500",
  Unset: "bg-gray-300 dark:bg-slate-500",
};
// Project types are free-form, so they cycle through a fixed palette.
const TYPE_BARS = ["bg-indigo-500", "bg-sky-500", "bg-violet-500", "bg-teal-500", "bg-slate-400"];
const MAX_TYPES = 5;

const sortByPriority = (rows) =>
  [...rows].sort((a, b) => {
    const ai = PRIORITY_ORDER.indexOf(a.label);
    const bi = PRIORITY_ORDER.indexOf(b.label);
    return (ai === -1 ? PRIORITY_ORDER.length : ai) - (bi === -1 ? PRIORITY_ORDER.length : bi);
  });

// Keeps the largest types and folds the rest into "Other".
const topTypes = (rows) => {
  if (rows.length <= MAX_TYPES) return rows;
  const head = rows.slice(0, MAX_TYPES - 1);
  const other = rows.slice(MAX_TYPES - 1).reduce((sum, r) => sum + r.count, 0);
  return [...head, { label: "Other", count: other }];
};

function BarList({ title, rows, total, colorFor }) {
  return (
    <div>
      <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-app-muted">
        {title}
      </h4>
      {rows.length === 0 ? (
        <p className="text-xs text-app-muted">No active projects.</p>
      ) : (
        <ul className="space-y-2.5">
          {rows.map((row, i) => {
            const pct = total ? Math.round((row.count / total) * 100) : 0;
            return (
              <li key={row.label}>
                <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                  <span className="truncate font-medium text-app-secondary" title={row.label}>
                    {row.label}
                  </span>
                  <span className="shrink-0 tabular-nums text-app-muted">
                    <span className="font-semibold text-app-primary">{row.count}</span> · {pct}%
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-slate-700">
                  <div className={`h-full rounded-full ${colorFor(row, i)}`} style={{ width: `${pct}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function SkeletonBars() {
  return (
    <div className="space-y-3" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <div key={i}>
          <div className="mb-1 h-3 w-24 animate-pulse rounded bg-gray-200 dark:bg-slate-700" />
          <div className="h-2 w-full animate-pulse rounded-full bg-gray-100 dark:bg-slate-700/60" />
        </div>
      ))}
    </div>
  );
}

/** Breakdown of active projects by priority and by type. */
function ProjectMixCard({ byPriority = [], byType = [], total = 0, loading = false, error = false }) {
  let body;
  if (loading) body = <SkeletonBars />;
  else if (error) body = <p className="text-sm text-app-error">{"Couldn't load projects."}</p>;
  else
    body = (
      <div className="space-y-6">
        <BarList
          title="By priority"
          rows={sortByPriority(byPriority)}
          total={total}
          colorFor={(row) => PRIORITY_BARS[row.label] || PRIORITY_BARS.Unset}
        />
        <BarList
          title="By type"
          rows={topTypes(byType)}
          total={total}
          colorFor={(_, i) => TYPE_BARS[i % TYPE_BARS.length]}
        />
      </div>
    );

  return (
    <DashboardPanel
      title="Project Mix"
      description={loading || error ? undefined : `${total} active project${total === 1 ? "" : "s"}`}
      action={{ label: "View", to: "/admin/project-management" }}
    >
      {body}
    </DashboardPanel>
  );
}

export default ProjectMixCard;
